/**
 * The three-step tour of the KPI dashboard (DOP-609).
 *
 * Runs once, the first time a dashboard with data is opened in this browser,
 * and again whenever "Take the tour" is pressed. Each step dims the page and
 * lifts ONE thing out of it — the cell key, the grid, the Charts tab — with a
 * card that says what it is for in a sentence or two. The Summit call's lesson
 * was that the dashboard's best features were invisible until someone pointed
 * at them; this is the pointing.
 *
 * The spotlight, keyboard and placement live in `Tour/SpotlightTour`; this
 * file is the KPI dashboard's steps.
 */

import React from "react";
import SpotlightTour, { type TourStep } from "../Tour/SpotlightTour";

const STEPS: readonly TourStep[] = [
	{
		anchor: '[data-tour="cell-guide"]',
		title: "The way a number looks tells you how sure we are",
		body: "A plain number was checked against your file. A dotted underline means Doppler found it but couldn't check it yet, so give it a look before it goes in a memo.",
		note: "This guide stays above the table. Close it any time; Take the tour brings it back.",
		next: "Next: where numbers come from",
	},
	{
		anchor: '[data-tour="kpi-grid"]',
		title: "Every number links to its source",
		body: "Click any number to open the file it came from, at that exact cell. Hover over a number first to see what its look means.",
		note: "To reorder rows, drag the ⋮⋮ handle beside a row or section name. Your order is saved for you.",
		next: "Next: see it as a chart",
	},
	{
		anchor: '[data-tab-id="charts"]',
		title: "See the trend in Charts",
		body: "The Charts tab draws these same numbers over time. Click any point on a chart to open the file behind it.",
		next: "Got it",
	},
];

export interface KpiTourProps {
	onClose: () => void;
}

const KpiTour: React.FC<KpiTourProps> = ({ onClose }) => (
	<SpotlightTour steps={STEPS} onClose={onClose} testId="kpi-tour-backdrop" />
);

export default KpiTour;
